import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  TrendingUp,
  TrendingDown,
  Eye,
  Settings,
  FileText,
  BookOpen,
  ChevronDown,
  LogOut,
  Menu,
  X,
  Clock,
} from "lucide-react";
import SirixSidebarPanel from "@/components/SirixSidebarPanel";
import {TenantBrand} from '../../branding/BrandingContext';

const SirixLayout = ({ children, user, onLogout, tabsOverride, hideSidePanel = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close mobile menu on route change (but keep dropdown open)
  useEffect(() => {
    setMobileMenuOpen(false);
    // Don't close dropdown here - let it stay open for submenu navigation
  }, [location.pathname]);

  const tabs = tabsOverride || [
    { path: "/", icon: Home, label: "HOME" },
    { 
      path: "/accounts", 
      icon: Users, 
      label: "ACCOUNTS",
      hasDropdown: true,
      dropdownItems: [
        { path: "/accounts", label: "All Accounts" },
      ]
    },
    { 
      path: "/open-positions", 
      icon: TrendingUp, 
      label: "OPEN POSITIONS",
      hasDropdown: true,
      dropdownItems: [
        { path: "/open-positions", label: "All Positions" },
      ]
    },
    { path: "/pending-orders", icon: Clock, label: "PENDING ORDERS" },
    { path: "/closed-positions", icon: TrendingDown, label: "CLOSED POSITIONS" },
    { path: "/exposure", icon: Eye, label: "EXPOSURE" },
    { 
      path: "/trading-terms", 
      icon: Settings, 
      label: "TRADING TERMS",
      hasDropdown: true,
      dropdownItems: [
        { path: "/trading-terms", label: "Trading Terms" },
        { path: "/trading-terms/instruments", label: "Instruments" },
        { path: "/trading-sessions", label: "Trading Sessions" },
      ]
    },
    { 
      path: "/reports", 
      icon: FileText, 
      label: "REPORTS",
      hasDropdown: true,
      dropdownItems: [
        { path: "/reports/accounts", label: "Reports - Accounts" },
        { path: "/reports/positions", label: "Reports - Open Positions" },
        { path: "/reports/transfers", label: "Reports - Money Transfers" },
      ]
    },
    { path: "/journal", icon: BookOpen, label: "JOURNAL" },
  ];

  const isActive = (path) => {
    if (path === "/" || (tabsOverride && path === "/admin/manager")) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleTabClick = (tab, e) => {
    if (tab.hasDropdown) {
      // Navigate to the tab's default page
      navigate(tab.path);
    } else {
      navigate(tab.path);
      setOpenDropdown(null);
      setMobileMenuOpen(false);
    }
  };

  const handleDropdownToggle = (tab, e) => {
    e.stopPropagation();
    e.preventDefault();
    // Toggle dropdown
    if (openDropdown === tab.path) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(tab.path);
    }
  };

  const handleDropdownItemClick = (path) => {
    navigate(path);
    setOpenDropdown(null);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col">
      {/* Top Header Bar */}
      <header className="sirix-header">
        {/* Mobile menu button */}
        {(isMobile || isTablet) && (
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 mr-2 hover:bg-white/10 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} />
          </button>
        )}
        
        <div className="text-white font-bold text-lg tracking-wide">
          <TenantBrand id="manager-header-brand" fallback="Yönetim Paneli"/>
        </div>
        
        {/* Desktop header info */}
        <div className={`flex items-center gap-2 sm:gap-4 lg:gap-6 text-xs sm:text-sm ${isMobile ? 'hidden' : ''}`}>
          {!tabsOverride&&<span className="hidden md:inline">Ortam: <strong>Gerçek</strong></span>}
          <span data-testid="manager-header-time">Saat: <strong>{currentTime.toLocaleTimeString('tr-TR')}</strong></span>
          <span className="hidden lg:inline">Sürüm: 26.3.600.0</span>
          <div className="hidden md:flex items-center gap-2">
            {!tabsOverride&&<><span>Haber Modu:</span><div className="bg-gray-700 rounded-full px-2 py-0.5 text-xs">Kapalı</div></>}
          </div>
          <button 
            onClick={onLogout}
            className="text-[#00BCD4] hover:underline flex items-center gap-1"
            data-testid="logout-btn"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Çıkış Yap</span>
          </button>
        </div>
        
        {/* Mobile: Just logout button */}
        {isMobile && (
          <button 
            onClick={onLogout}
            className="text-[#00BCD4] hover:underline flex items-center gap-1 ml-auto"
            data-testid="logout-btn-mobile"
          >
            <LogOut size={18} />
          </button>
        )}
      </header>

      {/* Mobile Navigation Drawer */}
      {(isMobile || isTablet) && (
        <>
          {/* Overlay */}
          {mobileMenuOpen && (
            <button
              className="fixed inset-0 bg-black/50 z-50"
              aria-label="Menüyü kapat"
              data-testid="manager-mobile-menu-overlay"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
          
          {/* Drawer */}
          <div 
            className={`fixed top-0 left-0 h-full w-72 bg-[#37474F] z-50 transform transition-transform duration-300 ease-in-out ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Drawer Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-white/10 bg-[#263238]">
              <div className="text-white font-bold text-lg">
                Yönetim Paneli
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-white hover:bg-white/10 rounded-lg"
                aria-label="Menüyü kapat"
                data-testid="manager-close-mobile-menu"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Drawer Navigation */}
            <nav className="flex-1 py-4 overflow-y-auto">
              {tabs.map((tab) => (
                <div key={tab.path}>
                  <div
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                      isActive(tab.path) 
                        ? 'bg-[#00BCD4]/20 text-[#00BCD4] border-r-2 border-[#00BCD4]' 
                        : 'text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <button 
                      onClick={(e) => handleTabClick(tab, e)}
                      className="flex items-center gap-3 flex-1"
                      data-testid={`manager-mobile-nav-${tab.path.split('/').filter(Boolean).join('-') || 'home'}`}
                    >
                      <tab.icon size={18} />
                      <span className="flex-1 text-left">{tab.label}</span>
                    </button>
                    {tab.hasDropdown && (
                      <button
                        onClick={(e) => handleDropdownToggle(tab, e)}
                        className="p-2 hover:bg-white/10 rounded"
                        data-testid={`manager-mobile-dropdown-${tab.path.split('/').filter(Boolean).join('-')}`}
                      >
                        <ChevronDown 
                          size={16} 
                          className={`transition-transform ${openDropdown === tab.path ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}
                  </div>
                  
                  {/* Dropdown items */}
                  {tab.hasDropdown && openDropdown === tab.path && (
                    <div className="bg-black/20">
                      {tab.dropdownItems.map((item) => (
                        <button
                          key={item.path}
                          onClick={() => handleDropdownItemClick(item.path)}
                          className="w-full flex items-center gap-3 px-8 py-2.5 text-sm text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                          data-testid={`manager-mobile-subnav-${item.path.split('/').filter(Boolean).join('-')}`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            
            {/* Drawer Footer */}
            <div className="p-4 border-t border-white/10">
              <div className="text-xs text-gray-400 space-y-1">
                {!tabsOverride&&<p>Ortam: Gerçek</p>}
                <p>Sürüm: 26.3.600.0</p>
                <p>{user?.email || "admin@broker.com"}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* SiRiX Style Sidebar Panel - Hidden on mobile */}
      {!hideSidePanel && !isMobile && !isTablet && <SirixSidebarPanel />}

      <div className="flex flex-1 overflow-hidden">
        {/* Main Content Area - with left padding for the vertical tab bar on desktop */}
        <div className={`flex-1 flex flex-col overflow-hidden ${!hideSidePanel && !isMobile && !isTablet ? 'ml-7' : ''}`}>
          {/* Tab Navigation - Hidden on mobile/tablet (use drawer instead) */}
          {!isMobile && !isTablet && (
            <nav className="sirix-tabs overflow-x-auto overflow-y-visible">
              {tabs.map((tab) => (
                <div key={tab.path} className="relative flex-shrink-0">
                  <div className={`sirix-tab ${isActive(tab.path) ? "active" : ""} flex items-center`}>
                    <button
                      onClick={(e) => handleTabClick(tab, e)}
                      className="flex items-center gap-1"
                      data-testid={`nav-${tab.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      <tab.icon size={16} />
                      <span className="whitespace-nowrap">{tab.label}</span>
                    </button>
                    {tab.hasDropdown && (
                      <button 
                        onClick={(e) => handleDropdownToggle(tab, e)}
                        className="p-1 ml-1 hover:bg-white/20 rounded"
                        data-testid={`nav-${tab.label.toLowerCase().replace(/[^a-z]/g, "-")}-dropdown`}
                      >
                        <ChevronDown 
                          size={14} 
                          className={`transition-transform ${openDropdown === tab.path ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}
                  </div>
                  
                  {/* Dropdown Menu */}
                  {tab.hasDropdown && openDropdown === tab.path && (
                    <div className="sirix-dropdown-menu" style={{ zIndex: 999 }}>
                      {tab.dropdownItems.map((item) => (
                        <div
                          key={item.path}
                          className="sirix-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDropdownItemClick(item.path);
                          }}
                        >
                          {item.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          )}

          {/* Page Content */}
          <main className="sirix-main flex-1 overflow-auto p-2 sm:p-4">
            {children}
          </main>
        </div>
      </div>

      {/* Status Bar - Hidden on mobile */}
      <footer className={`sirix-status-bar ${isMobile ? 'hidden' : ''}`}>
        <div className="sirix-connection-status">
          {tabsOverride?<span data-testid="manager-footer-workspace">Personel çalışma alanı</span>:<><div className="sirix-connection-dot"></div><span>Connected to application server</span></>}
        </div>
        <div>
          {user?.email || "admin@broker.com"}
        </div>
      </footer>

      {/* Click outside to close dropdown - Desktop only (positioned below dropdown) */}
      {openDropdown && !isMobile && !isTablet && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </div>
  );
};

export default SirixLayout;
